import { useEffect, useRef } from "react";
import { useAuth } from "./auth";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const EVENT_BUS = new EventTarget();

/**
 * Subscribe to a realtime event ('chat:new', 'chat:sent', 'support:new',
 * 'support:reply'). The callback receives the event payload.
 *
 * Usage:
 *   useRealtimeEvent("chat:new", (data) => { reload(); });
 */
export function useRealtimeEvent(name, handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const fn = (e) => ref.current?.(e.detail);
    EVENT_BUS.addEventListener(name, fn);
    return () => EVENT_BUS.removeEventListener(name, fn);
  }, [name]);
}

/**
 * Single global EventSource for the logged-in user. Re-connects with
 * exponential backoff. Dispatches events on the internal bus so multiple
 * components can subscribe without opening multiple connections.
 */
export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const esRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;
    let cancelled = false;
    const token = localStorage.getItem("eme_token");

    function connect() {
      if (cancelled || !user || !token) return;
      const url = `${API_BASE}/api/realtime/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;

      const dispatch = (name, evt) => {
        let data = {};
        try {
          data = evt.data ? JSON.parse(evt.data) : {};
        } catch {
          /* ignore */
        }
        EVENT_BUS.dispatchEvent(new CustomEvent(name, { detail: data }));
      };

      es.addEventListener("hello", (e) => {
        retryRef.current = 0;
        dispatch("realtime:hello", e);
      });
      ["chat:new", "chat:sent", "support:new", "support:reply", "pharmacy:welcome", "notification:new", "presence:update", "payment:created", "payment:updated", "prescription_new", "prescription:created", "prescription:status_changed", "scan:updated"].forEach((name) => {
        es.addEventListener(name, (e) => dispatch(name, e));
      });

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (closedRef.current) return;
        const delay = Math.min(15000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      cancelled = true;
      closedRef.current = true;
      esRef.current?.close();
      esRef.current = null;
    };
  }, [user]);

  return children;
}
